import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { VideoAssetsService } from '../../services/video-assets/video-assets.service';
import { VideoRequestsService } from '../../services/video-requests/video-requests.service';
import { Video } from '../../services/video-requests/video.model';
import { ViewVideoRequest } from '../../services/video-requests/view-video-request.model';
import { WindowService } from '../../services/window/window.service';
import { ReturnUrlService } from '../../services/return-url/return-url.service';
import * as moment from 'moment';

@Component({
    selector: 'app-video-request',
    templateUrl: './video-request.component.html',
    styleUrls: ['./video-request.component.css']
})
export class VideoRequestComponent implements OnInit {
    activeVideo?: Video;
    playCounted: boolean[] = [];
    reason: string;
    request: ViewVideoRequest;

    constructor(private activatedRoute: ActivatedRoute,
        private videoAssetsService: VideoAssetsService,
        private videoRequestsService: VideoRequestsService,
        private returnUrlService: ReturnUrlService,
        private windowService: WindowService) {
        this.reason = '';
        this.returnUrlService.save();
    }

    canDownload(video: Video): boolean {
        const canDownloadValue = (!this.request.isFreeDownload)
            && (video.status === 'Emailed')
            && (this.request.purgedIn > 0);
        return canDownloadValue;
    }

    canReject(video: Video): boolean {
        return (this.request.purgedIn > 0)
            && (!this.request.isFreeDownload)
            && (video.playCount < 1)
            && (video.canReject)
            && (video.status === 'Emailed');
    }

    isReasonValid(): boolean {
        return this.reason && !this.reason.match(/^ *$/);
    }

    get hoursAgo(): string {
        const createdAt = moment.parseZone(this.request.createdAt);
        const now = moment();
        const difference = now.diff(createdAt, 'hours');

        if (difference < 1) {
            return '(< 1 hour ago)';
        }

        return (difference < 24) ? `(${difference} hours ago)` : null;
    }

    async ngOnInit(): Promise<void> {
        const requestIdString = this.activatedRoute.snapshot
            ? this.activatedRoute.snapshot.paramMap.get('requestId')
            : null;

        if (requestIdString) {
            this.request = await this.videoRequestsService.get(requestIdString);
        }
    }

    async onDownload(event: any, video: Video): Promise<void> {
        event.preventDefault();
        video.canReject = false;
        await this.videoAssetsService.setHasBeenDownloaded(video.assetId);
        this.windowService.window.location.href = video.highQualityVideoUri;
    }

    async onEnded(video: Video, videoTag: HTMLVideoElement): Promise<void> {
        // The end of the video always increments the count if it hasn't been done already
        if (!(this.playCounted[video.assetId] || false)) {
            await this.incrementPlayCount(video);
        }
        this.playCounted[video.assetId] = false; // Reset the marker so they have 30 seconds for the next "play"
        videoTag.currentTime = 0;
        videoTag.load();
    }

    async onPause(video: Video, videoTag: HTMLVideoElement): Promise<void> {
        await this.incrementPlayCountIfRequired(video, videoTag);
    }

    async onPlay(video: Video, videoTag: HTMLVideoElement): Promise<void> {
        await this.incrementPlayCountIfRequired(video, videoTag);
    }

    async reject(): Promise<void> {
        this.activeVideo.canReject = false;
        this.activeVideo.rejectionReason = this.reason;
        this.activeVideo.state = 'Rejected';
        this.activeVideo.status = 'Rejected';

        const assetId = this.activeVideo.assetId;
        const rejectReason = this.reason;

        await this.videoAssetsService.reject(assetId, rejectReason);

        this.activeVideo = null;
        this.reason = '';
    }

    showRejectModal(video: Video): void {
        this.activeVideo = video;
        this.reason = '';
        (<HTMLVideoElement>document.getElementById("videoplayer_" + video.assetId)).pause();
    }

    private calculatePlayedSeconds(playedRanges: TimeRanges): number {
        if (playedRanges == null || playedRanges.length === 0) {
            return 0;
        }
        let totalDuration = 0;
        for (let playedIndex = 0; playedIndex < playedRanges.length; playedIndex++) {
            const rangeDuration = playedRanges.end(playedIndex) - playedRanges.start(playedIndex);
            totalDuration += (isNaN(rangeDuration) ? 0 : rangeDuration);
        }
        return totalDuration;
    }

    private async incrementPlayCount(video: Video): Promise<void> {
        video.playCount++;
        await this.videoAssetsService.incrementPlayCount(video.assetId);
        this.playCounted[video.assetId] = true;
    }

    private async incrementPlayCountIfRequired(video: Video, videoTag: HTMLVideoElement): Promise<void> {
        let shouldIncrementPlayCount = true; // Keep current behaviour by default

        if (videoTag != null) {
            const playedSeconds = this.calculatePlayedSeconds(videoTag.played);

            if (isNaN(playedSeconds) || (playedSeconds === Infinity)) {
                shouldIncrementPlayCount = true;
            } else {
                shouldIncrementPlayCount = (playedSeconds >= 30);
            }
        }

        if (!(this.playCounted[video.assetId] || false) && shouldIncrementPlayCount) {
            await this.incrementPlayCount(video);
        }
    }
}
